import { useEffect } from "react";

import {
  TYPE_LOG_LAYER,
  TYPE_LOG_VERTICAL_LAYER,
  WELL_LAYER,
  WELL_LAYER_POINT,
  WELL_SOURCE_LAYER
} from "constants/mapLayers.constants";
import { AnyLayer } from "mapbox-gl";
import {
  selectDisplaySettings,
  selectTypeLogWells,
  selectWellLegendItems,
  useMapStore
} from "stores/map";

import { getTypeLogZoomRange } from "components/map/utils/entitiesOnMapFetchPolicy";

const TYPE_LOG_LAYER_SOURCES = [
  [TYPE_LOG_LAYER, WELL_LAYER],
  [TYPE_LOG_VERTICAL_LAYER, WELL_LAYER_POINT]
] as const;

const buildTypeLogLayer = (
  id: string,
  source: string,
  filter: unknown[],
  [minzoom, maxzoom]: readonly [number, number]
) =>
  ({
    id,
    type: "symbol",
    source,
    "source-layer": WELL_SOURCE_LAYER,
    // Below HZ_RASTER_MVT_CROSSOVER_ZOOM this layer is what keeps the well vector tiles loading.
    minzoom,
    maxzoom,
    filter,
    paint: {
      "icon-color": "black"
    },
    layout: {
      "icon-image": "type-log",
      "icon-size": [
        "interpolate",
        ["linear"],
        ["zoom"],
        6,
        0.15 / 2,
        10,
        1.0 / 6,
        12,
        1.0 / 6,
        22,
        1.0 / 6
      ],
      "icon-allow-overlap": true,
      "icon-ignore-placement": true,
      "icon-rotate": ["get", "angle"],
      "icon-rotation-alignment": "map",
      "icon-keep-upright": true,
      "icon-offset": [-90, -130] // Offset the icon to the top right
    }
  } as AnyLayer);

export default function useTypeLogWellsLayer(
  mapbox: mapboxgl.Map,
  hasTypeLogWidget: boolean,
  addLayer
) {
  const typeLogWells = useMapStore(selectTypeLogWells);
  // Sole writer of these layers' zoom range (exempt in useManageMapboxLayerStyles).
  const [minzoom, maxzoom] = getTypeLogZoomRange(
    useMapStore(selectWellLegendItems),
    useMapStore(selectDisplaySettings).layerZoomSettings?.[TYPE_LOG_LAYER]
  );

  useEffect(() => {
    if (!mapbox) return;

    if (!hasTypeLogWidget || typeLogWells.length === 0) {
      TYPE_LOG_LAYER_SOURCES.forEach(([id]) => {
        if (mapbox.getLayer(id)) mapbox.removeLayer(id);
      });
      return;
    }

    const ids = Object.fromEntries(typeLogWells.map((item) => [item.id, 1]));
    const filter = ["has", ["get", "Uwi"], ["literal", ids]];

    const sourcesReady = () =>
      !!mapbox.getSource(WELL_LAYER) && !!mapbox.getSource(WELL_LAYER_POINT);

    const apply = () => {
      TYPE_LOG_LAYER_SOURCES.forEach(([id, source]) => {
        if (mapbox.getLayer(id)) {
          mapbox.setFilter(id, filter as mapboxgl.Expression);
          mapbox.setLayerZoomRange(id, minzoom, maxzoom);
        } else {
          addLayer(buildTypeLogLayer(id, source, filter, [minzoom, maxzoom]));
        }
      });
    };

    if (sourcesReady()) apply();

    // A basemap switch (setStyle) drops these layers; re-add once the well sources return.
    const onSourceData = () => {
      const missing = TYPE_LOG_LAYER_SOURCES.some(([id]) => !mapbox.getLayer(id));
      if (missing && sourcesReady()) apply();
    };
    mapbox.on("sourcedata", onSourceData);
    return () => {
      mapbox.off("sourcedata", onSourceData);
    };
  }, [mapbox, typeLogWells, hasTypeLogWidget, minzoom, maxzoom]);
}
